import React, { useEffect, useState } from 'react';
import {
  Row,
  Col,
  Card,
  Button,
  Modal,
  Form,
  Input,
  InputNumber,
  Switch,
  Checkbox,
  Space,
  Typography,
  Spin,
  Alert,
  Popconfirm
} from 'antd';
import {
  EditOutlined,
  DeleteOutlined,
  PlusOutlined,
  CreditCardOutlined,
  DollarOutlined,
  BuildOutlined
} from '@ant-design/icons';
import { adminService } from '../../services/api';

const { Title, Text } = Typography;

const SCREEN_OPTIONS = [
  { label: 'Dashboard', value: 'dashboard' },
  { label: 'Facturas', value: 'invoices' },
  { label: 'Productos/Servicios', value: 'products' },
  { label: 'Clientes', value: 'clients' },
  { label: 'Empleados', value: 'employees' },
  { label: 'Configuración', value: 'settings' },
  { label: 'Créditos', value: 'credits' },
  { label: 'Nóminas', value: 'nominas' },
  { label: 'Cálculo de impuestos', value: 'tax-calculation' },
  { label: 'Cobranza (PPD)', value: 'cobranza-ppd' },
  { label: 'Autoportal', value: 'autofactura-configs' },
];

const AdminPlans = () => {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalVisible, setModalVisible] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [error, setError] = useState('');
  const [form] = Form.useForm();

  useEffect(() => {
    fetchPlans();
  }, []);

  const fetchPlans = async () => {
    try {
      const plansData = await adminService.getPlans();
      setPlans(plansData);
    } catch (error) {
      setError('Error al cargar los planes');
      console.error('Error fetching plans:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEditPlan = (plan) => {
    setSelectedPlan(plan);
    const allowedScreens = Array.isArray(plan?.allowed_screens) ? plan.allowed_screens : [];
    form.setFieldsValue({ ...plan, allowed_screens: allowedScreens });
    setModalVisible(true);
  };

  const handleNewPlan = () => {
    setSelectedPlan(null);
    form.resetFields();
    form.setFieldsValue({
      name: '',
      description: '',
      monthly_credits: 0,
      monthly_price: 0,
      max_companies: 1,
      level: 1,
      allowed_screens: [],
      is_active: true
    });
    setModalVisible(true);
  };

  const handleCloseModal = () => {
    setModalVisible(false);
    setSelectedPlan(null);
    form.resetFields();
  };

  const handleSavePlan = async (values) => {
    try {
      if (selectedPlan?.id) {
        await adminService.updatePlan(selectedPlan.id, values);
      } else {
        await adminService.createPlan(values);
      }
      await fetchPlans();
      handleCloseModal();
    } catch (error) {
      setError('Error al guardar el plan');
      console.error('Error saving plan:', error);
    }
  };

  const handleDeletePlan = async (planId) => {
    try {
      await adminService.deletePlan(planId);
      await fetchPlans();
    } catch (error) {
      setError('Error al eliminar el plan');
      console.error('Error deleting plan:', error);
    }
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '50px' }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <Space align="center">
          <CreditCardOutlined style={{ fontSize: 24 }} />
          <Title level={2} style={{ margin: 0 }}>
            Gestión de Planes
          </Title>
        </Space>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={handleNewPlan}
        >
          Nuevo Plan
        </Button>
      </div>

      {error && (
        <Alert
          message={error}
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
        />
      )}

      {/* Cards de planes */}
      <Row gutter={[16, 16]}>
        {plans.map((plan) => (
          <Col xs={24} sm={12} md={8} lg={6} key={plan.id}>
            <Card
              title={plan.name}
              extra={
                <Space>
                  <Button
                    type="text"
                    icon={<EditOutlined />}
                    onClick={() => handleEditPlan(plan)}
                  />
                  <Popconfirm
                    title="¿Estás seguro de que quieres eliminar este plan?"
                    onConfirm={() => handleDeletePlan(plan.id)}
                    okText="Sí"
                    cancelText="No"
                  >
                    <Button
                      type="text"
                      danger
                      icon={<DeleteOutlined />}
                    />
                  </Popconfirm>
                </Space>
              }
              style={{ height: '100%' }}
            >
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <Text type="secondary">{plan.description}</Text>
                <Space direction="vertical" size="small">
                  <Space>
                    <CreditCardOutlined />
                    <Text>{plan.monthly_credits} créditos/mes</Text>
                  </Space>
                  <Space>
                    <DollarOutlined />
                    <Text>${plan.monthly_price}/mes</Text>
                  </Space>
                  <Space>
                    <BuildOutlined />
                    <Text>{plan.max_companies >= 2000000000 ? 'Ilimitadas' : plan.max_companies} empresas</Text>
                  </Space>
                </Space>
              </div>
            </Card>
          </Col>
        ))}
      </Row>

      {/* Modal para editar/crear plan */}
      <Modal
        title={selectedPlan?.id ? 'Editar Plan' : 'Nuevo Plan'}
        open={modalVisible}
        onCancel={handleCloseModal}
        footer={null}
        width={600}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSavePlan}
        >
          <Form.Item
            name="name"
            label="Nombre del plan"
            rules={[{ required: true, message: 'Por favor ingresa el nombre del plan' }]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            name="description"
            label="Descripción"
            rules={[{ required: true, message: 'Por favor ingresa la descripción' }]}
          >
            <Input.TextArea rows={3} />
          </Form.Item>

          <Form.Item
            name="allowed_screens"
            label="Pantallas habilitadas"
            extra="Si no seleccionas ninguna pantalla, el plan tendrá acceso a todas."
          >
            <Checkbox.Group options={SCREEN_OPTIONS} style={{ width: '100%' }} />
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="monthly_credits"
                label="Créditos mensuales"
                rules={[{ required: true, message: 'Por favor ingresa los créditos mensuales' }]}
              >
                <InputNumber min={0} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="monthly_price"
                label="Precio mensual"
                rules={[{ required: true, message: 'Por favor ingresa el precio mensual' }]}
              >
                <InputNumber min={0} step={0.01} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="max_companies"
                label="Límite de Empresas"
                rules={[{ required: true, message: 'Por favor ingresa el límite' }]}
              >
                <InputNumber min={1} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="level"
                label="Nivel"
                rules={[{ required: true, message: 'Por favor ingresa el nivel' }]}
              >
                <InputNumber min={1} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="is_active"
                label="Estado"
                valuePropName="checked"
              >
                <Switch checkedChildren="Activo" unCheckedChildren="Inactivo" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button onClick={handleCloseModal}>
                Cancelar
              </Button>
              <Button type="primary" htmlType="submit">
                Guardar
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default AdminPlans;
